import React, { useMemo, useState } from "react"
import { Control, FormState } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Input } from "reactstrap"
import {
    addYears,
    DateInput,
    FieldsetFormGroup,
    FloatingLabelInput,
    FormGroupWithCheckbox,
    IFormBase,
    InputLabel,
    ShowErrorMessages,
    StandardParagraph,
    TClearError,
    TRegister,
    TSetError,
    valFuncRequired,
} from "swiipe.portal.shared"

import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./DigitealPersonFields.scss"

export interface IDigitealKycPerson {
    id?: string
    firstName: string
    lastName: string
    dateOfBirth: string
    placeOfBirth: string
    isPep: boolean
}

const personFieldKeys: (keyof IDigitealKycPerson)[] = ["firstName", "lastName", "dateOfBirth", "placeOfBirth"]

interface IDigitealPersonFieldsProps<TForm extends IFormBase> {
    control: Control<TForm>
    register: TRegister
    setError: TSetError<any>
    clearError: TClearError<any>
    formState: FormState<TForm>
    showErrors: boolean
    fieldSelectorPrefix: string
    alreadyUploadedFileNames?: {
        pictureLegitimation: string[]
        addressLegitimation: string[]
    }
    saveJustTriggered: boolean
}

const DigitealPersonFields = <TForm extends IFormBase>({
    control,
    register,
    setError,
    clearError,
    formState,
    showErrors,
    fieldSelectorPrefix,
    alreadyUploadedFileNames,
    saveJustTriggered,
}: IDigitealPersonFieldsProps<TForm>) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()

    const [selectedCountry, setSelectedCountry] = useState("DK")

    const getFieldSelector = (fieldName: string) => fieldSelectorPrefix + fieldName

    const dateOfBirthValueDateFormat = "yyyy-MM-dd"
    const dateOfBirthMaxDate = useMemo(() => addYears(new Date(), -16), [])

    const errorNamesToDisplay = useMemo(() => personFieldKeys.map((k) => getFieldSelector(k)), [])

    return (
        <div className="digiteal-person-fields">
            <Input type="text" name={getFieldSelector("id")} innerRef={register} hidden />
            <FieldsetFormGroup field>
                <InputLabel title={t("common.firstName.label")}>
                    <FloatingLabelInput
                        name={getFieldSelector("firstName")}
                        placeholder={t("common.firstName.label")}
                        innerRef={register(valFuncRequired(t("common.firstName.errorMessage")))}
                        hidePlaceholder
                    />
                </InputLabel>
            </FieldsetFormGroup>
            <FieldsetFormGroup field>
                <InputLabel title={t("common.lastName.label")}>
                    <FloatingLabelInput
                        name={getFieldSelector("lastName")}
                        placeholder={t("common.lastName.label")}
                        innerRef={register(valFuncRequired(t("common.lastName.errorMessage")))}
                        hidePlaceholder
                    />
                </InputLabel>
            </FieldsetFormGroup>
            <FieldsetFormGroup field>
                <InputLabel title={t("digitealKyc.dateOfBirth.label")}>
                    <DateInput
                        required
                        name={getFieldSelector("dateOfBirth")}
                        errorMessage={t("digitealKyc.dateOfBirth.errorMessage")}
                        country={selectedCountry}
                        formControl={control}
                        valueDateFormat={dateOfBirthValueDateFormat}
                        addFormatToPlaceholder
                        maxDate={dateOfBirthMaxDate}
                        helpTypeSpecialChars
                    />
                </InputLabel>
            </FieldsetFormGroup>
            <FieldsetFormGroup field>
                <InputLabel title={t("digitealKyc.placeOfBirth.label")}>
                    <FloatingLabelInput
                        name={getFieldSelector("placeOfBirth")}
                        placeholder={t("digitealKyc.placeOfBirth.label")}
                        innerRef={register(valFuncRequired(t("digitealKyc.placeOfBirth.errorMessage")))}
                        hidePlaceholder
                    />
                </InputLabel>
            </FieldsetFormGroup>
            <FormGroupWithCheckbox
                innerRef={control.register()}
                name={getFieldSelector("isPep")}
                hint={t("digitealKyc.pep.hint")}
            >
                <StandardParagraph className="my-0 d-inline-block">{t("digitealKyc.ubos.isPep")}</StandardParagraph>
            </FormGroupWithCheckbox>
            <ShowErrorMessages
                className="digiteal-person-fields__error-messages"
                isHidden={!showErrors}
                formState={formState}
                onlyForFieldNames={errorNamesToDisplay}
            />
        </div>
    )
}

export default DigitealPersonFields
